import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SiblingItem } from '../../types/nav';
import { NAV_PILLAR_COLORS } from '../../constants/navPillars';
import { COLORS } from '../../constants/theme';

/**
 * SF-128 Component 2: reusable sibling-pill-row shell primitive, sibling to `GlanceCard`/
 * `MobileTabBar` in `components/shell/`. Mounting is gated at the call site (`Layout.tsx`), not
 * via an internal CSS class — this component carries no `lg:hidden` of its own.
 *
 * Placement is `position: sticky` (not floating, not plain in-flow) — `Layout.tsx`'s content
 * column has no height cap, so the document scrolls, and an in-flow row would scroll out of view
 * on the first swipe on pages already confirmed to overflow the mobile viewport. `z-index: 20`
 * never actually contends with `Layout.tsx`'s mobile-only Home-return/Profile/Logout floating
 * buttons (`z-30`) — those are suppressed on exactly the sibling-group pages (SF-146:
 * `findSiblingGroup`-resolved routes) this component renders on, so the two are mutually
 * exclusive, never mounted at the same time. Opaque background is required for the same reason
 * the "sticky first column" contract requires one for sticky table cells (no opaque bg means
 * scrolled-under content bleeds through). At <=360px the row scrolls horizontally
 * (`overflow-x-auto`) rather than shrinking pills below a 44px tap-height / 12px+ label-text floor.
 *
 * SF-146: `ariaLabel` is a required prop (no default) — a default would silently reproduce the
 * hardcoded "Realm sections" bug for the next group (Kingdom/World) that reuses this component.
 */
interface SiblingRowProps {
  items: readonly SiblingItem[];
  activeRoute: string;
  ariaLabel: string;
}

export function SiblingRow({ items, activeRoute, ariaLabel }: SiblingRowProps) {
  const activeLabel = items.find((item) => item.route === activeRoute)?.label;
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (activeLabel) {
      setAnnouncement(`Now viewing ${activeLabel}`);
    }
  }, [activeRoute, activeLabel]);

  return (
    <nav
      aria-label={ariaLabel}
      className="flex items-stretch overflow-x-auto"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        backgroundColor: COLORS.bg,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {items.map((item) => {
        const isActive = item.route === activeRoute;
        const pillar = NAV_PILLAR_COLORS[item.pillar];

        return (
          <Link
            key={item.key}
            to={item.route}
            aria-current={isActive ? 'page' : undefined}
            className="flex items-center justify-center gap-1 flex-shrink-0"
            style={{
              minHeight: 44,
              minWidth: 44,
              padding: '6px 14px',
              borderBottom: isActive ? `2px solid ${pillar.primary}` : '2px solid transparent',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
              <path
                d={item.iconPath}
                stroke={isActive ? pillar.primary : COLORS.text.tertiary}
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span
              style={{
                fontSize: 12,
                color: isActive ? COLORS.text.primary : COLORS.text.tertiary,
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {item.label}
            </span>
          </Link>
        );
      })}
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </nav>
  );
}
